import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'

const AuthTest = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, checkAuth, login, logout } = useStore()
  const [tests, setTests] = useState([])

  const runTests = () => {
    const results = []

    // Test 1: localStorage session
    const localSession = localStorage.getItem('admin_session_simple')
    results.push({
      name: 'Session dans localStorage',
      passed: !!localSession,
      details: localSession ? JSON.parse(localSession).email : 'Aucune session'
    })

    // Test 2: Store isAuthenticated
    results.push({
      name: 'Store isAuthenticated',
      passed: isAuthenticated,
      details: isAuthenticated ? 'True' : 'False'
    })

    // Test 3: User object
    results.push({
      name: 'User object',
      passed: !!user,
      details: user ? `${user.email} (${user.role})` : 'Null'
    })

    // Test 4: User role
    results.push({
      name: 'User role = admin',
      passed: user?.role === 'admin',
      details: user?.role || 'Aucun rôle'
    })

    setTests(results)
  }

  useEffect(() => {
    runTests()
  }, [user, isAuthenticated])

  const handleForceLogin = () => {
    const sessionData = {
      id: 'admin-1',
      email: 'admin@residencemassena.com',
      name: 'Stéphanne Masséna',
      role: 'admin',
      permissions: ['all'],
      loginTime: Date.now(),
      sessionId: 'test-session-' + Date.now(),
      lastActivity: Date.now()
    }

    localStorage.setItem('admin_session_simple', JSON.stringify(sessionData))
    login(sessionData)
    runTests()
  }

  const handleCheckAuth = () => {
    checkAuth()
    setTimeout(() => runTests(), 100)
  }

  const handleClearAll = () => {
    localStorage.clear()
    logout()
    runTests()
  }

  const handleGoToDashboard = () => {
    navigate('/admin')
  }

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-green-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto">
        <GlassCard className="p-8">
          <h1 className="text-3xl font-bold text-black mb-6">
            Test d'Authentification
          </h1>

          {/* Tests */}
          <div className="space-y-3 mb-6">
            {tests.map((test, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  test.passed
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {test.passed ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                    <div>
                      <h3 className="font-semibold text-black">{test.name}</h3>
                      <p className="text-sm text-gray-600">{test.details}</p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      test.passed
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}
                  >
                    {test.passed ? 'OK' : 'FAIL'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={handleForceLogin}
              className="w-full"
            >
              Forcer la Connexion
            </Button>

            <Button
              variant="secondary"
              onClick={handleCheckAuth}
              icon={RefreshCw}
              className="w-full"
            >
              Vérifier l'Authentification
            </Button>

            <Button
              variant="secondary"
              onClick={handleGoToDashboard}
              className="w-full"
            >
              Aller au Dashboard
            </Button>

            <Button
              variant="secondary"
              onClick={handleClearAll}
              className="w-full bg-red-100 text-red-700 hover:bg-red-200"
            >
              Tout Effacer
            </Button>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">
              Comment utiliser cette page :
            </h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Cliquez sur "Forcer la Connexion" pour créer une session</li>
              <li>Vérifiez que tous les tests sont verts</li>
              <li>Cliquez sur "Aller au Dashboard"</li>
              <li>Si vous êtes redirigé vers login, revenez ici et recommencez</li>
            </ol>
          </div>

          {/* Debug Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">
              Informations de Debug :
            </h3>
            <div className="space-y-2 text-sm font-mono">
              <div>
                <span className="text-gray-600">isAuthenticated:</span>{' '}
                <span className={isAuthenticated ? 'text-green-600' : 'text-red-600'}>
                  {isAuthenticated ? 'true' : 'false'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">user:</span>{' '}
                <span className="text-gray-900">
                  {user ? JSON.stringify(user, null, 2) : 'null'}
                </span>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  )
}

export default AuthTest
